import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { TooltipRoot, tooltipTipOffset, tooltipTipWidth } from './Tooltip.styled';

type Side = 'top' | 'bottom' | 'left' | 'right';

type Props = {
	/** The line's items, in order. Leave it out and nest `TooltipGroupItem`s in `children`
	 *  instead when the triggers come from somewhere else — they can sit at any depth. */
	items?: { message: string; element: ReactNode }[];
	direction?: 'horizontal' | 'vertical';
	/** Which side of the line the chip sits on. Defaults to across the line. */
	placement?: Side;
	gap?: number;
}

// ponytail: reuses the single Tooltip chip and swaps its animation for a
// transition — one element slides between triggers, so there is nothing to
// mount, no popper, no exit timer. `$exiting` stays false; the chip is never
// interactive, and never flips to the other side either — a group is laid out
// by the caller, who knows which side has room.
const SlidingChip = styled(TooltipRoot)`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  animation: none;
  transition: translate ${props => props.theme.motion.duration.fast}
      ${props => props.theme.motion.easing.snappy},
    opacity ${props => props.theme.motion.duration.default}
      ${props => props.theme.motion.easing.snappy};

  /* Popper centres the arrow; without it, centre it by hand. */
  &[data-popper-placement='top'] .tooltip-arrow,
  &[data-popper-placement='bottom'] .tooltip-arrow {
    left: calc(50% - ${tooltipTipWidth / 2}px);
  }

  &[data-popper-placement='left'] .tooltip-arrow,
  &[data-popper-placement='right'] .tooltip-arrow {
    top: calc(50% - ${tooltipTipWidth / 2}px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Line = styled.div<{ $vertical: boolean; $gap: number }>`
  position: relative;
  display: inline-flex;
  flex-direction: ${props => props.$vertical ? 'column' : 'row'};
  align-items: center;
  gap: ${props => props.$gap}px;
`;

// Offsets come off the trigger's own box against the line's, so the chip lands
// on the trigger whatever the items' sizes — or their depth, for a group whose
// triggers are nested inside markup it does not own. `Line` is the offset parent.
function chipTranslate(line: HTMLElement, trigger: HTMLElement, side: Side) {
	const lineBox = line.getBoundingClientRect();
	const { left, top, width: w, height: h } = trigger.getBoundingClientRect();
	const x = left - lineBox.left;
	const y = top - lineBox.top;

	switch (side) {
	case 'top':
		return `calc(${x + w / 2}px - 50%) calc(${y - tooltipTipOffset}px - 100%)`;
	case 'bottom':
		return `calc(${x + w / 2}px - 50%) ${y + h + tooltipTipOffset}px`;
	case 'left':
		return `calc(${x - tooltipTipOffset}px - 100%) calc(${y + h / 2}px - 50%)`;
	case 'right':
		return `${x + w + tooltipTipOffset}px calc(${y + h / 2}px - 50%)`;
	}
}

type GroupContextValue = {
	id: string;
	activeTrigger: HTMLElement | null;
	visible: boolean;
	show: (trigger: HTMLElement, message: string) => void;
	hide: (trigger: HTMLElement) => void;
}

const GroupContext = createContext<GroupContextValue | null>(null);

/** One trigger of a `TooltipGroup`, at any depth. Renders its child alone outside a group. */
export function TooltipGroupItem({ message, children }: PropsWithChildren<{ message: string }>) {
	const group = useContext(GroupContext);
	const ref = useRef<HTMLSpanElement>(null);

	const show = () => ref.current && group?.show(ref.current, message);
	const hide = () => ref.current && group?.hide(ref.current);

	if (!group) {
		return <>{children}</>;
	}

	return (
		<span
			ref={ref}
			aria-describedby={group.visible && group.activeTrigger === ref.current ? group.id : undefined}
			onMouseEnter={show}
			onFocus={show}
			onMouseLeave={hide}
			onBlur={hide}>
			{children}
		</span>
	);
}

let groupCount = 0;

export function TooltipGroup({ items, children, direction = 'horizontal', placement, gap = 8 }
	: PropsWithChildren<Props>) {
	const vertical = direction === 'vertical';
	const side = placement ?? (vertical ? 'left' : 'top');
	const lineRef = useRef<HTMLDivElement>(null);
	const [id] = useState(() => `tooltip-group-${++groupCount}`);

	// The trigger outlives the hover so the chip keeps its text and position while
	// it fades out, instead of snapping back to the first item.
	const [{ message, trigger, visible }, setActive] = useState<{
		message: string, trigger: HTMLElement | null, visible: boolean
	}>({ message: '', trigger: null, visible: false });

	const show = useCallback(
		(hovered: HTMLElement, hoveredMessage: string) =>
			setActive({ message: hoveredMessage, trigger: hovered, visible: true }),
		[]
	);

	const hide = useCallback(
		(left: HTMLElement) => setActive(a => a.trigger === left ? { ...a, visible: false } : a),
		[]
	);

	const group = useMemo(
		() => ({ id, activeTrigger: trigger, visible, show, hide }),
		[id, trigger, visible, show, hide]
	);

	// Measured on every render rather than on hover: the line can reflow — an item
	// resizing, one appearing — while the chip is up, and it has to follow.
	// Not gated on `visible`: the chip holds its last position while it fades,
	// instead of flying back to the origin on the way out.
	const translate = trigger && lineRef.current ? chipTranslate(lineRef.current, trigger, side) : undefined;

	return (
		<GroupContext.Provider value={group}>
			<Line ref={lineRef} $vertical={vertical} $gap={gap}>
				{items?.map(({ message: itemMessage, element }, i) => (
					<TooltipGroupItem key={i} message={itemMessage}>{element}</TooltipGroupItem>
				))}
				{children}
				<SlidingChip
					$exiting={false}
					id={id}
					role="tooltip"
					aria-hidden={!visible}
					data-popper-placement={side}
					style={{ opacity: visible ? 1 : 0, translate }}>
					{message}
					<div className="tooltip-arrow" />
				</SlidingChip>
			</Line>
		</GroupContext.Provider>
	);
}
